import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  IconButtonComponent,
  IPaginationEvent,
  ModalService,
  PaginationComponent,
  TableComponent,
  ToastService,
} from '@ui-lib/components';
import { debounceTime, filter } from 'rxjs';
import { ConfirmationDialogComponent } from '../../shared/components/confirm-dialog/confirmation-dialog.component';
import { IConfirmationDialogData } from '../../shared/components/confirm-dialog/confirmation-dialog-data.interface';
import { LoadingState } from '../../shared/types/loading.type';
import { ServiceCenterModalComponent } from './components/service-center-modal/service-center-modal.component';
import {
  IServiceCentersFilterForm,
  ServiceCenterItem,
  ServiceCenterModalAction,
} from './service-centers.model';
import { ServiceCentersService } from './service-centers.service';
import { SERVICE_CENTERS_TABLE_COLUMNS } from './service-centers-table-columns.config';
import { PermissionService } from '../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../core/models/user-permissions.enum';
import { PageToolbarComponent } from '../../shared/components/page-toolbar/page-toolbar.component';
import { PageToolbarAction } from '../../shared/components/page-toolbar/page-toolbar.model';

@Component({
  standalone: true,
  selector: 'mon-service-centers',
  templateUrl: './service-centers.component.html',
  styleUrl: './service-centers.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ServiceCentersService],
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    TranslateModule,
    IconButtonComponent,
    PaginationComponent,
    TableComponent,
    PageToolbarComponent,
  ],
})
export class ServiceCentersComponent implements OnInit {
  private readonly serviceCentersService = inject(ServiceCentersService);
  private readonly modalService = inject(ModalService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(PermissionService);

  protected readonly filterForm = new FormGroup<IServiceCentersFilterForm>({
    keyword: new FormControl('', { nonNullable: true }),
  });

  protected readonly columns = SERVICE_CENTERS_TABLE_COLUMNS;
  protected readonly LoadingState = LoadingState;
  protected readonly ServiceCenterModalAction = ServiceCenterModalAction;
  protected readonly serviceCenters$ = this.serviceCentersService.serviceCenters$;
  protected readonly serviceCentersLoadingStatus =
    this.serviceCentersService.serviceCentersLoadingStatus;
  protected readonly totalCount = this.serviceCentersService.totalCount;
  protected readonly currentPage = signal<number>(1);
  protected readonly canManageMaintenance = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.MaintenanceManage),
    { initialValue: false },
  );

  protected get toolbarActions(): PageToolbarAction[] {
    return this.canManageMaintenance()
      ? [
          {
            id: 'create',
            icon: 'plus',
            titleTranslateKey: 'monitoring.service-centers.actions.add',
          },
        ]
      : [];
  }

  protected onToolbarAction(actionId: string): void {
    if (actionId === 'create') {
      this.openServiceCenterModal(ServiceCenterModalAction.Create);
    }
  }

  public ngOnInit(): void {
    this.filterForm.controls.keyword.valueChanges
      .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe((keyword) => {
        this.currentPage.set(1);
        this.serviceCentersService.updateServiceCentersRequest({
          skip: 0,
          keyword: keyword.trim() || null,
        });
      });
  }

  protected readonly asServiceCenter = (item: unknown): ServiceCenterItem =>
    item as ServiceCenterItem;

  protected clearFilters(): void {
    this.filterForm.controls.keyword.setValue('');
  }

  protected get searchControl(): FormControl<string> {
    return this.filterForm.controls.keyword;
  }

  protected onPageChange(event: IPaginationEvent): void {
    this.currentPage.set(event.pageIndex);
    this.serviceCentersService.updateServiceCentersRequest({
      skip: (event.pageIndex - 1) * event.pageSize,
      take: event.pageSize,
    });
  }

  protected openServiceCenterModal(
    action: ServiceCenterModalAction,
    serviceCenter?: ServiceCenterItem,
  ): void {
    if (!this.canManageMaintenance()) {
      return;
    }

    this.modalService
      .open({
        component: ServiceCenterModalComponent,
        data: {
          action,
          serviceCenter,
        },
      })
      .afterClose$.pipe(
        filter((result) => !!result),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.toastService.success({
          message: this.translateService.instant(
            action === ServiceCenterModalAction.Create
              ? 'monitoring.service-centers.toast.success.created'
              : 'monitoring.service-centers.toast.success.updated',
          ),
        });
        this.serviceCentersService.reloadServiceCenters();
      });
  }

  protected confirmDelete(serviceCenter: ServiceCenterItem): void {
    if (!this.canManageMaintenance()) {
      return;
    }

    this.modalService
      .open<IConfirmationDialogData>({
        component: ConfirmationDialogComponent,
        data: {
          headerTranslationKey: 'monitoring.service-centers.dialog.delete.title',
          descriptionTranslationKey: 'monitoring.service-centers.dialog.delete.description',
          descriptionTranslationParams: { name: serviceCenter.name },
          approvalTranslationKey: 'monitoring.service-centers.dialog.delete.confirm',
          cancellationTranslationKey: 'monitoring.service-centers.dialog.delete.cancel',
        },
      })
      .afterClose$.pipe(
        filter((result) => !!result),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.deleteServiceCenter(serviceCenter));
  }

  private deleteServiceCenter(serviceCenter: ServiceCenterItem): void {
    if (!this.canManageMaintenance()) {
      return;
    }

    this.serviceCentersService
      .deleteServiceCenter(serviceCenter.id)
      .pipe(
        filter((result) => !!result),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.toastService.success({
          message: this.translateService.instant(
            'monitoring.service-centers.toast.success.deleted',
          ),
        });
        this.serviceCentersService.reloadServiceCenters();
      });
  }
}
